import { Injectable } from "@angular/core";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IAccount } from "@app/services/account.service";
import { VehicleService } from "@app/services/vehicle.service";
import { firstValueFrom } from "rxjs";
import { AccountService } from "../../_services/account.service";
import { DeviceHealthService } from "./device-health.service";
import { DeviceHealthRecordHelper } from "./helpers/device-health-record-helper";
import { IDeviceHealthAccountSummary } from "./models/device-health-account-summary";
import { DeviceHealthSimpleSummary } from "./models/device-health-simple-summary";

@Injectable({
    providedIn: 'root'
})
export class DeviceHealthAccountService {
    constructor(
        private readonly deviceHealthService: DeviceHealthService,
        private readonly accountService: AccountService,
        private readonly vehicleService: VehicleService
    ) { }

    async getStatsForAccounts(): Promise<IDeviceHealthAccountSummary[]> {
        const summaries = await firstValueFrom(this.deviceHealthService.getAllSummaries());
        const accounts = await firstValueFrom(this.accountService.getCustomerAccounts());
        const vehicles = await this.vehicleService.getVehicles();

        const { telematicSummaries, cameraSummaries } = this.groupSummariesByType(summaries);
        const vehiclesByAccount = vehicles.groupBy(x => x.AccountId);

        const result = accounts
            .map(account => this.mapDataToAccount(account, telematicSummaries, cameraSummaries, vehiclesByAccount))
            .filter(summary => summary.vehiclesCount > 0);

        return result;
    }

    private groupSummariesByType(
        summaries: DeviceHealthSimpleSummary[]
    ): {telematicSummaries: DeviceHealthSimpleSummary[], cameraSummaries: DeviceHealthSimpleSummary[] } {
        const telematicSummaries: DeviceHealthSimpleSummary[] = [];
        const cameraSummaries: DeviceHealthSimpleSummary[] = [];

        summaries.forEach(s => {
            const isTelematic = s.DaySummaries.some(
                ds => ds.TypeSummaries
                    .map(ts => ts.Type)
                    .some(t => DeviceHealthRecordHelper.isTelematic(t))
            )

            if (isTelematic) {
                telematicSummaries.push(s);
            } else {
                cameraSummaries.push(s);
            }
        });

        return { telematicSummaries, cameraSummaries };
    }

    private mapDataToAccount(
        account: IAccount, 
        telematicSummaries: DeviceHealthSimpleSummary[], 
        cameraSummaries: DeviceHealthSimpleSummary[], 
        vehicles: Record<number, IVehicle[]>
    ): IDeviceHealthAccountSummary {
        const vehiclesCount = vehicles[account.Id]?.length || 0;
        const cameraFailingVehiclesCount = this.getFailingVehiclesCount(account.Id, cameraSummaries);
        const telematicFailingVehiclesCount = this.getFailingVehiclesCount(account.Id, telematicSummaries);

        return { 
            accountId: account.Id, 
            account, 
            vehiclesCount, 
            cameraFailingVehiclesCount, 
            telematicFailingVehiclesCount 
        };
    }

    private getFailingVehiclesCount(accountId: number, summaries: DeviceHealthSimpleSummary[]): number {
        const summariesForAccount = summaries.filter(s => s.AccountId === accountId);
        const summariesByVehicle: Record<number, DeviceHealthSimpleSummary[]> = summariesForAccount.groupBy(x => x.VehicleId);
        return Object.keys(summariesByVehicle).length;
    }
}